/**
 * visual-game 用户端、编辑端和运营端共享的本地 token store，
 * 由 deviceKey/devicePrefix 区分各应用的设备标识。
 *
 * 为何不接 `@drama/auth-client`：vg 的登录由页面 composable 直接驱动——`api.auth.login/register`
 * 拿到 token 后手动 `tokenStore.setTokens`，登出手动 `clear`，匿名合流读 `getDeviceId()` 当
 * anonymousId，没有 auth-client 的响应式 session / 路由守卫 / flows 那套。这与 auth-client
 * 「session 唯一归属」的模型不兼容，重接需改页面层(超出本次边界)，故按任务回退合并成一份。
 *
 * refresh 请求由调用端注入，token store 只负责持久化和轮转，不自行发送网络请求。
 */
const ACCESS_KEY = 'visualGame.accessToken'
const REFRESH_KEY = 'visualGame.refreshToken'

function read(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key, value) {
  try {
    if (value == null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // Storage can be unavailable in private/test contexts.
  }
}

function stableDeviceId(deviceKey, prefix) {
  const current = read(deviceKey)
  if (current) return current
  const next = `${prefix}${Math.random().toString(36).slice(2)}`
  write(deviceKey, next)
  return next
}

/**
 * @param {object} [opts]
 * @param {(request: { refresh_token: string }) => Promise<{ access_token: string, refresh_token: string }>} opts.refreshSession
 * @param {string} [opts.deviceKey] deviceId 的 storage key(vg 与 vg-studio 各用一个，互不覆盖)
 * @param {string} [opts.devicePrefix] 新 deviceId 前缀
 */
export function createLocalTokenStore({
  refreshSession,
  deviceKey = 'visualGame.deviceId',
  devicePrefix = 'vg-dev-',
} = {}) {
  const deviceId = stableDeviceId(deviceKey, devicePrefix)

  // token 每次直接读 localStorage、不在内存缓存：第三方登录的授权小窗写入的 token，主页面才看得到
  function setTokens(tokens = {}) {
    write(ACCESS_KEY, tokens.access_token || null)
    write(REFRESH_KEY, tokens.refresh_token || null)
  }

  function clear() {
    write(ACCESS_KEY, null)
    write(REFRESH_KEY, null)
  }

  async function refresh() {
    const refreshToken = read(REFRESH_KEY)
    if (!refreshToken) throw new Error('No refresh token')
    setTokens(await refreshSession({ refresh_token: refreshToken }))
    return read(ACCESS_KEY)
  }

  return {
    getAccessToken: () => read(ACCESS_KEY),
    getRefreshToken: () => read(REFRESH_KEY),
    getDeviceId: () => deviceId,
    hasSession: () => read(ACCESS_KEY) !== null || read(REFRESH_KEY) !== null,
    setTokens,
    clear,
    refresh,
  }
}
